import type { GridPaginationModel } from "@mui/x-data-grid";

/**
 * Hierarchical key factories, mirroring apps/mobile/src/constants/queryKeys.ts.
 *
 * The hierarchy is what makes invalidation cheap: invalidating
 * `queryKeys.patients.all` clears every patient list and detail, whatever the
 * pagination or filters.
 */
/**
 * Pagination plus whatever filters a list takes. Typed as `object` rather than
 * a Record: GridPaginationModel is an interface, and interfaces get no
 * implicit index signature, so `Record<string, unknown>` would reject the very
 * value every list hook passes.
 */
type ListParams = GridPaginationModel | object;

export const queryKeys = {
  me: ["me"] as const,

  users: {
    all: ["users"] as const,
    list: (params: ListParams) => ["users", "list", params] as const,
    detail: (id: string) => ["users", "detail", id] as const,
  },

  patients: {
    all: ["patients"] as const,
    list: (params: ListParams) => ["patients", "list", params] as const,
    detail: (id: string) => ["patients", "detail", id] as const,
    surveys: (id: string) => ["patients", "detail", id, "surveys"] as const,
    /**
     * One submitted answer set. Under the patient rather than under the
     * schedule because that is what the read is addressed by, and it is
     * immutable once written: a submission is first-write-wins, so nothing
     * invalidates this.
     */
    surveyResponse: (id: string, scheduleId: string, occurrenceKey: string) =>
      ["patients", "detail", id, "responses", scheduleId, occurrenceKey] as const,
    studies: (id: string, params: ListParams) =>
      ["patients", "detail", id, "studies", params] as const,
  },

  studies: {
    all: ["studies"] as const,
    list: (params: ListParams) => ["studies", "list", params] as const,
    detail: (id: string) => ["studies", "detail", id] as const,
    patients: (id: string, params: ListParams) =>
      ["studies", "detail", id, "patients", params] as const,
    members: (id: string) => ["studies", "detail", id, "members"] as const,
  },

  surveys: {
    all: ["surveys"] as const,
    list: (params: ListParams) => ["surveys", "list", params] as const,
    detail: (id: string) => ["surveys", "detail", id] as const,
  },

  analysis: {
    all: ["analysis"] as const,
    /** Keyed by scope AND questionnaire: a page shows one, and a read that
     *  scored the whole scope to render one would be the waste this narrowing
     *  exists to avoid. */
    patientStudy: (patientStudyId: string, surveyId: string) =>
      ["analysis", "patient-study", patientStudyId, surveyId] as const,
    /** The other scope: a patient's own questionnaires, outside any study. */
    patientMonitoring: (patientId: string, surveyId: string) =>
      ["analysis", "patient-monitoring", patientId, surveyId] as const,
    /** A study's whole cohort, one questionnaire. Under `analysis` rather than
     *  under `studies` so scoring invalidations reach it. */
    study: (studyId: string, surveyId: string) =>
      ["analysis", "study", studyId, surveyId] as const,
    plugins: ["analysis", "plugins"] as const,
  },

  hospital: ["hospital"] as const,

  accessLog: {
    all: ["accessLog"] as const,
    list: (params: ListParams) => ["accessLog", "list", params] as const,
  },

  auditLog: {
    all: ["auditLog"] as const,
    list: (params: ListParams) => ["auditLog", "list", params] as const,
  },
} as const;
